import React from "react";
import { Mail, Star } from "lucide-react";

const InboxMessageCard = ({
  messages,
  onClick,
  selectedMessage,
  starredMessages,
  onToggleStar,
}) => {
  const getInitials = (name) =>
    name?.split(" ").slice(0, 2).map((n) => n[0]).join("") || "";

  const getPriorityLabel = (priority) => {
    if (!priority) return null;
    const p = priority.toLowerCase();
    if (p === "high priority") return "high";
    if (p === "medium priority") return "medium";
    if (p === "low priority") return "low";
    return p;
  };

  const getPriorityClass = (priority) =>
    priority?.toLowerCase().replace(/\s+/g, "-");

if (!messages?.length) {
  return (
    <div style={{ minWidth: "100%", display: "table" }}>
      <div className="space-y-2">
        <div className="text-center py-12">
          <Mail className="w-16 h-16 mx-auto mb-3 opacity-50" />
          <p className="text-[14px]">
            No messages found
          </p>
          <p className="text-[12px] mt-1">
            Try adjusting your search or filters
          </p>
        </div>
      </div>
    </div>
  );
}


  return (
    <div className="mt-1 space-y-4">
      {messages.map((message) => {
        const isSelected = selectedMessage?.id === message.id;
        const isRead = message.isRead;
        const priorityLabel = getPriorityLabel(message.priority);
        const priorityClass = getPriorityClass(message.priority);
        const isStarred = starredMessages?.[message.id] || false;

        let cardClass = "flex flex-col gap-3 p-3 rounded-xl border-2 cursor-pointer transition-shadow ";
        if (isSelected) {
          cardClass += "bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-500 shadow-lg";
        } else if (!isRead) {
          cardClass += "bg-white dark:bg-zinc-800 border-gray-300 hover:shadow-md"; 
        } else {
          cardClass += "bg-gray-50 dark:bg-zinc-900 border-gray-200 hover:shadow-md";
        }

        return (
          <div key={message.id} onClick={() => onClick(message)} className={cardClass}>
            <div className="flex justify-between gap-1">
              <div>
                <span
                  className={`relative flex size-10 shrink-0 overflow-hidden rounded-full w-9 h-9 border-2 border-white dark:border-zinc-700 shadow-sm flex-shrink-0 ${
                    isSelected ? "bg-gradient-to-br from-blue-500 to-indigo-500 text-white font-semibold flex items-center justify-center" : ""
                  }`}
                >
                  {!isSelected && (
                    <span className="flex size-full items-center justify-center rounded-full text-[11px] font-semibold bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                      {getInitials(message.sender)}
                    </span>
                  )}
                  {isSelected && getInitials(message.sender)}
                </span>
              </div>

              <div className="space-y-2 flex-1">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex-1 overflow-hidden pr-1">
                    <p className={`text-[12px] break-words font-semibold ${isRead ? "dark:text-zinc-100 font-medium" : "dark:text-zinc-100"}`}>
                      {message.sender || "Unknown"}
                    </p>
                    <p className={`text-[11px] break-words ${isRead ? "dark:text-zinc-200 font-normal" : "dark:text-zinc-200 font-semibold"}`}>
                      {message.title || ""}
                    </p>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleStar(message.id);
                    }}
                    className={`inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] rounded-md h-5 w-5 flex-shrink-0 ${
                      isStarred
                        ? ""
                        : "[&_svg]:pointer-events-none text-slate-400 dark:text-zinc-500"
                    }`}
                  >
                    <Star className={`w-3 h-3 ${isStarred ? "fill-yellow-500 text-yellow-500" : ""}`} />
                  </button>
                </div>

                <p className="text-[10px]  dark:text-zinc-400 line-clamp-2 mb-2 break-words">
                  {message.content || ""}
                </p>

                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-1 flex-wrap">
                    {priorityLabel && (
                      <span className={`text-[9px] font-medium status-indicator ${priorityClass}`}>
                        {priorityLabel}
                      </span>
                    )}

                    {message.attachments?.length > 0 && (
                      <span className="px-1.5 py-0.5 text-[9px] font-medium bg-gray-100 dark:bg-gray-700 rounded">
                        📎 {message.attachments.length}
                      </span>
                    )}
                  </div>

                  <span className="text-[9px]  dark:text-zinc-500">
                    {message.time || ""}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default InboxMessageCard;
